"use client";

import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import type { User as HubAppUserRecord } from "@/lib/types/user";
import { useSearchEmployeesFTS } from "@/lib/hooks/api/dashboard/dashboard-graphql";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AppUser } from "@west-tennessee-consulting/supabase";
import { useFloatingDropdown } from "./shared/useFloatingDropdown";
import { UserOptionPreview } from "./shared/UserOptionPreview";
import { FloatingDropdownPanel } from "./shared/FloatingDropdownPanel";
import { InlineOptionRow } from "./shared/InlineOptionRow";

const normalizeUserIds = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: unknown): string | null => {
        if (typeof item === "string" && item.length > 0) return item;
        if (typeof item === "object" && item !== null && "id" in item && typeof (item as { id: string }).id === "string") return (item as { id: string }).id;
        return null;
      })
      .filter((id): id is string => id !== null);
  }
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
};

interface OptionItem {
  value: string;
  label: string;
  searchableText: string;
  // Rich user data for rendering
  email?: string;
  available_hours?: number;
  is_active?: boolean;
  is_billable?: boolean;
  hourly_rate?: number;
}

interface MultiUserInlineSelectProps {
  currentUserIds?: string[] | null;
  onUsersSelect: (userIds: string[]) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  showUnassigned?: boolean;
  maxUsers?: number;
  pageSize?: number;
}

/**
 * Multi-user select with the same behavior and design as CompanyInlineSelect:
 * single Input as trigger, fixed dropdown below (no header), same styling, type-to-search.
 */
export function MultiUserInlineSelect({
  currentUserIds = EMPTY_ARRAY,
  onUsersSelect,
  placeholder = "Assign owners...",
  disabled = false,
  showUnassigned = true,
  maxUsers = 5,
  pageSize = 30,
}: MultiUserInlineSelectProps) {
  const { containerRef, isOpen, setIsOpen, dropdownStyle, handleFocus, handleBlur } = useFloatingDropdown();
  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });
  const [currentUserIdFromAuth, setCurrentUserIdFromAuth] = useState<string | null>(null);

  const normalizedUserIds = useMemo(() => normalizeUserIds(currentUserIds), [currentUserIds]);

  /** Resolve chip labels for IDs outside the current FTS window */
  const { data: userDirectory } = useAdminUsers();

  const { users, isLoading: isFetching } = useSearchEmployeesFTS(
    debouncedSearchValue,
    pageSize,
    true
  );

  useEffect(() => {
    void (async () => {
      try {
        const { data: { user } } = await getBrowserSupabaseClient().auth.getUser();
        if (user?.id) setCurrentUserIdFromAuth(user.id);
      } catch { /* ignore */ }
    })();
  }, []);

  const options: OptionItem[] = useMemo(() => {
    const list: OptionItem[] = [];
    const seen = new Set<string>();

    list.push({ value: "me", label: "Assign to Me", searchableText: "me assign myself" });
    seen.add("me");

    if (showUnassigned) {
      list.push({ value: "unassigned", label: "Unassigned", searchableText: "unassigned none" });
      seen.add("unassigned");
    }

    normalizedUserIds.forEach((id) => {
      if (seen.has(id)) return;
      const row = userDirectory?.find((u: HubAppUserRecord) => u.id === id);
      if (!row) return;
      const name = `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.email || "";
      list.push({ value: row.id, label: name || row.id, searchableText: [name, row.email].filter(Boolean).join(" "), email: row.email });
      seen.add(id);
    });

    (users || []).forEach((u) => {
      if (seen.has(u.id)) return;
      seen.add(u.id);
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "";
      const { available_hours, is_active, is_billable, hourly_rate, email } = u;
      list.push({
        value: u.id,
        label: name || u.id,
        searchableText: [name, email].filter(Boolean).join(" "),
        email,
        // The search endpoint returns SQL nulls; the option preview tests these
        // with `!== undefined`, so normalise null → undefined or it renders
        // "null hrs/week".
        available_hours: available_hours ?? undefined,
        is_active: is_active ?? undefined,
        is_billable: is_billable ?? undefined,
        hourly_rate: hourly_rate ?? undefined,
      });
    });
    return list;
  }, [users, showUnassigned, normalizedUserIds, userDirectory]);

  const resolveIds = useCallback(
    (ids: string[]) => ids.map((id) => (id === "me" && currentUserIdFromAuth ? currentUserIdFromAuth : id)).filter((id) => id !== "unassigned"),
    [currentUserIdFromAuth]
  );

  const handleToggle = useCallback(
    async (value: string) => {
      if (value === "unassigned") {
        await onUsersSelect([]);
        setSearchValue("");
        setIsOpen(false);
        return;
      }
      const resolved = value === "me" && currentUserIdFromAuth ? currentUserIdFromAuth : value;
      const current = normalizedUserIds.filter((id) => id !== "unassigned");
      const resolvedCurrent = resolveIds(current);
      const alreadySelected = resolvedCurrent.includes(resolved);
      let next: string[];
      if (alreadySelected) {
        next = current.filter((id) => (id === "me" && currentUserIdFromAuth ? currentUserIdFromAuth : id) !== resolved);
      } else {
        if (maxUsers && resolvedCurrent.length >= maxUsers) return;
        next = [...current, value];
      }
      await onUsersSelect(resolveIds(next));
      if (value !== "me" && value !== "unassigned") setSearchValue("");
    },
    [normalizedUserIds, currentUserIdFromAuth, maxUsers, onUsersSelect, resolveIds, setSearchValue, setIsOpen]
  );

  const displayValue = useMemo(() => {
    if (normalizedUserIds.length === 0) return "";
    return normalizedUserIds
      .map((id) => {
        if (id === "me") return "Assign to Me";
        if (id === "unassigned") return "Unassigned";
        return options.find((o) => o.value === id)?.label ?? id;
      })
      .filter(Boolean)
      .join(", ");
  }, [normalizedUserIds, options]);

  const filteredOptions = options.filter((opt) => {
    const s = (searchValue || "").toLowerCase();
    return !s || `${opt.label} ${opt.searchableText}`.toLowerCase().includes(s);
  });

  const isSelected = (value: string): boolean => {
    const resolved = value === "me" && currentUserIdFromAuth ? currentUserIdFromAuth : value;
    const resolvedCurrent = resolveIds(normalizedUserIds);
    if (value === "unassigned") return resolvedCurrent.length === 0;
    return resolvedCurrent.includes(resolved);
  };

  const selectedUsers = useMemo(
    () => normalizedUserIds.map((id) => options.find((o) => o.value === id)).filter((opt): opt is OptionItem => opt !== undefined),
    [normalizedUserIds, options]
  );

  const handleRemoveUser = useCallback(
    async (userId: string) => onUsersSelect(resolveIds(normalizedUserIds.filter((id) => id !== userId))),
    [normalizedUserIds, resolveIds, onUsersSelect]
  );

  return (
    <div ref={containerRef} className="relative w-full min-w-[160px]">
      <div className="relative">
        {/* Badge display when not focused */}
        {!isOpen && selectedUsers.length > 0 && (
          <div className="absolute inset-0 flex items-center gap-1 px-2 py-1 pointer-events-none overflow-hidden">
            <div className="flex flex-wrap gap-1 items-center">
              {selectedUsers.slice(0, 2).map((user) => (
                <Badge key={user.value} variant="secondary" className="text-xs">
                  {user.label}
                </Badge>
              ))}
              {selectedUsers.length > 2 && (
                <Badge variant="secondary" className="text-xs">
                  +{selectedUsers.length - 2}
                </Badge>
              )}
            </div>
          </div>
        )}
        <Input
          value={isOpen ? searchValue : displayValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("h-8 text-sm", !isOpen && selectedUsers.length > 0 && "text-transparent")}
          autoFocus
        />
      </div>
      <FloatingDropdownPanel
        isOpen={isOpen}
        style={dropdownStyle}
        isLoading={isFetching && filteredOptions.length <= 2}
        isEmpty={filteredOptions.length === 0}
        emptyText="No users found"
      >
        {filteredOptions.map((opt) => (
          <InlineOptionRow
            key={opt.value}
            value={opt.value}
            isSelected={isSelected(opt.value)}
            onSelect={handleToggle}
          >
            <UserOptionPreview {...opt} />
          </InlineOptionRow>
        ))}
      </FloatingDropdownPanel>
    </div>
  );
}
